/**
 * @file seo.config.ts
 * @path apps/website/src/config/seo.config.ts
 * @description
 */

// src/config/seo.ts
import { vacancies } from "../data";
import { services } from "../data";
import { activitiesData } from "../data";
import { Activity, SEOProps, Service, Vacancy } from "@sirbastians/types";
import { branding } from "./site.config";

const defaults = {
  title: "Sir Bastians Shuttle Service | Student, Airport & Cape Town Tours",
  description:
    "Reliable transport with Sir Bastians. We offer premium UWC & CPUT student shuttles, private Cape Town tours, and professional airport transfers.",
  keywords:
    "UWC shuttle service, CPUT transport, student transport Cape Town, Grassy Park shuttle, Ottery shuttle, Cape Town airport transfers, private Cape Town tours, Constantia wine tasting, Big Five safari Cape Town, corporate staff transport, Cape Point tour, Boulders Beach penguins, Garden Route tour, Stellenbosch wine tour, shark cage diving Cape Town",
  author: "Sir Bastians Shuttle Service",
  url: "https://sirbastians.co.za",
  image: branding.ogImage,
  twitterHandle: "@YourTwitterHandle",
};

export function generateSEO(props: SEOProps = {}) {
  const meta = { ...defaults, ...props };
  const pageTitle = props.title
    ? `${props.title} | Sir Bastians Shuttle Service`
    : defaults.title;

  return {
    title: pageTitle,
    description: meta.description,
    keywords: meta.keywords,
    author: meta.author,
    canonical: `${meta.url}${props.url || ""}`,
    og: {
      title: pageTitle,
      description: meta.description,
      type: "website",
      url: `${meta.url}${props.url || ""}`,
      site_name: "Sir Bastians Shuttle Service",
      image: meta.image,
      imageWidth: "1200",
      imageHeight: "630",
    },
    twitter: {
      card: "summary_large_image",
      title: pageTitle,
      description: meta.description,
      image: meta.image,
    },
  };
}

export const generateLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Sir Bastians Shuttle Service",
  description:
    "A premier transport provider in Cape Town offering dedicated student shuttles, private tours (including Cape Point, Winelands, and Safaris), and airport transfers.",
  image: defaults.image,
  url: defaults.url,
  telephone: "+27837001037",
  email: "sir.bastians@gmail.com",
  priceRange: "ZAR",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Cape Town",
    addressRegion: "WC",
    addressCountry: "ZA",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: "-33.9249",
    longitude: "18.4241",
  },
  areaServed: {
    "@type": "AdministrativeArea",
    name: "Western Cape",
  },
  openingHours: "Mo-Su 00:00-23:59",
});

export const generateServiceSchemas = () => {
  if (!services || services.length === 0) return null;

  const serviceItems = services.map((service: Service) => {
    const schemaType =
      service.category === "Tours" ? "TouristTrip" : "ShuttleService";
    const fullTitle = service.subtitle
      ? `${service.title}: ${service.subtitle}`
      : service.title;

    return {
      "@type": schemaType,
      name: fullTitle,
      description: service.shortDescription,
      url: `${defaults.url}/services/${service.slug}`,
      image: service.gallery.primary,
      provider: {
        "@type": "LocalBusiness",
        name: "Sir Bastians Shuttle Service",
      },
      offers: {
        "@type": "Offer",
        price: service.pricing.amount,
        priceCurrency: "ZAR",
      },
    };
  });

  return {
    "@context": "https://schema.org",
    "@graph": serviceItems,
  };
};

export const generateActivitySchemas = () => {
  if (!activitiesData || activitiesData.length === 0) return null;

  const activityItems = activitiesData.map((activity: Activity) => ({
    "@type": "TouristAttraction",
    name: activity.title,
    description: activity.shortDescription,
    url: `${defaults.url}/experiences/${activity.slug}`,
    image: activity.gallery.primary,
    offers: {
      "@type": "Offer",
      price: activity.pricing.amount,
      priceCurrency: activity.pricing.currency,
    },
  }));

  return {
    "@context": "https://schema.org",
    "@graph": activityItems,
  };
};

export const generateVacancySchemas = () => {
  if (!vacancies || vacancies.length === 0) {
    return null;
  }

  const jobPostings = vacancies.map((vacancy: Vacancy) => ({
    "@type": "JobPosting",
    title: vacancy.title,
    description: vacancy.description,
    identifier: {
      "@type": "PropertyValue",
      name: "Sir Bastians Shuttle Service",
      value: vacancy.id,
    },
    hiringOrganization: {
      "@type": "Organization",
      name: "Sir Bastians Shuttle Service",
      sameAs: defaults.url,
    },
    jobLocation: vacancy.jobLocation,
    employmentType: vacancy.employmentType,
    datePosted: vacancy.datePosted,
    validThrough: vacancy.validThrough,
    baseSalary: vacancy.baseSalary,
  }));

  return {
    "@context": "https://schema.org",
    "@graph": jobPostings,
  };
};

export const generateSingleServiceSchema = (slug: string) => {
  const service = services.find((s) => s.slug === slug);
  if (!service) return null;

  const schemaType =
    service.category === "Tours" ? "TouristTrip" : "ShuttleService";
  const fullTitle = service.subtitle
    ? `${service.title}: ${service.subtitle}`
    : service.title;

  return {
    "@context": "https://schema.org",
    "@type": schemaType,
    name: fullTitle,
    description: service.longDescription,
    url: `${defaults.url}/services/${service.slug}`,
    image: service.gallery.primary,
    provider: {
      "@type": "LocalBusiness",
      name: "Sir Bastians Shuttle Service",
    },
    itinerary: service.details.itinerary?.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "TouristAttraction",
        name: item.title,
        description: item.description,
      },
    })),
    offers: service.pricing.options.map((option) => ({
      "@type": "Offer",
      name: option.label,
      price: option.price.replace(/[^0-9]/g, ""),
      priceCurrency: "ZAR",
      description: option.description,
    })),
  };
};

export const generateSingleActivitySchema = (slug: string) => {
  const activity = activitiesData.find((a) => a.slug === slug);
  if (!activity) return null;

  return {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    name: activity.title,
    description: activity.longDescription,
    url: `${defaults.url}/experiences/${activity.slug}`,
    image: activity.gallery.primary,
    offers: {
      "@type": "Offer",
      price: activity.pricing.amount,
      priceCurrency: activity.pricing.currency,
      url: `${defaults.url}/experiences/${activity.slug}`,
    },
  };
};
